import type { ContentReviewScene } from '../services/contentReviewService';
import type { ShotCopySnapshot } from '../services/shotCopyService';

/** A selected beat belongs to one package and one measured clip span. */
export interface ShotCopyTarget {
  packageId: string;
  fingerprint: string;
  sceneId: string;
  clipId: string;
  offsetSec: number;
  durationSec: number;
}
const validId = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,160}$/.test(value);

export function selectableBeats(scenes: ContentReviewScene[] = []): ContentReviewScene[] {
  return scenes.filter(scene => validId(scene.id) && validId(scene.clip_id)
    && scenes.filter(other => other.id === scene.id).length === 1
    && typeof scene.offset_sec === 'number' && Number.isFinite(scene.offset_sec) && scene.offset_sec >= 0
    && typeof scene.duration_sec === 'number' && Number.isFinite(scene.duration_sec) && scene.duration_sec > 0);
}

export function shotCopyTarget(packageId: string, scene: ContentReviewScene, fingerprint: string): ShotCopyTarget {
  if (!validId(packageId) || !/^[a-f0-9]{64}$/.test(fingerprint) || selectableBeats([scene]).length !== 1) throw new Error('Invalid shot target.');
  return { packageId, fingerprint, sceneId: scene.id, clipId: scene.clip_id!, offsetSec: scene.offset_sec!, durationSec: scene.duration_sec! };
}

/** Fail closed if the selected clip disappeared or moved while opening the editor. */
export function selectedShot(snapshot: ShotCopySnapshot, target: ShotCopyTarget) {
  const matches = snapshot.scenes.filter(scene => scene.id === target.sceneId);
  const scene = matches[0];
  if (snapshot.fingerprint !== target.fingerprint || !/^[a-f0-9]{64}$/.test(target.fingerprint)
    || !validId(target.packageId) || !validId(target.sceneId) || !validId(target.clipId)
    || !Number.isFinite(target.offsetSec) || target.offsetSec < 0
    || !Number.isFinite(target.durationSec) || target.durationSec <= 0
    || snapshot.package_id !== target.packageId || matches.length !== 1 || !scene
    || scene.clip_id !== target.clipId || scene.offset_sec !== target.offsetSec
    || scene.duration_sec !== target.durationSec) {
    throw new Error('This beat changed. Close and reload the reel before editing.');
  }
  return scene;
}
